import moment from 'moment-timezone'
import {
  momentToISOLocalDate,
  momentToISOLocalDateTime,
  momentToISOLocalTime,
  parseMomentFromLocalDateAndLocalTimeFields
} from 'utils/date'
import { getNumberOfPointAtIndex, OrderPointType } from './OrderPointsForm'
import { IntermediatePointType } from 'components/Order/constants'
import { parseCoordinates, toMicroDegrees } from 'utils/utils'
import { v4 as uuid4 } from 'uuid'
import { scheduledEmailsFrequencyOptionFromEmailNotificationSettings, } from 'components/Order/utils'

export function deliveryPointWithTimeFrameStartingToday (dp) {
  const startMoment = moment(dp.fromDateTime)
  const newStartMoment = moment()
    .hours(startMoment.hours())
    .minutes(startMoment.minutes())
  return deliveryPointWithTimeFrameStartingAt(dp, newStartMoment)
}

export function deliveryPointWithTimeFrameStartingAt (dp, timeFrameStartMoment) {
  const oldFromMoment = moment(dp.fromDateTime)
  const oldToMoment = moment(dp.toDateTime)
  const frameDuration = oldToMoment.diff(oldFromMoment)
  const newFromMoment = timeFrameStartMoment.clone()
  const newToMoment = timeFrameStartMoment.clone().add(frameDuration)
  return {
    ...dp,
    fromDateTime: momentToISOLocalDateTime(newFromMoment),
    toDateTime: momentToISOLocalDateTime(newToMoment)
  }
}

export function mapDeliveryPointToFormValues (dp, type) {
  const fromMoment = moment(dp.fromDateTime)
  const toMoment = moment(dp.toDateTime)
  const { cargoDetails } = dp
  const { quantity, weight } = cargoDetails
  return {
    id: uuid4(),
    type,
    deliveryPointDetails: {
      ...dp,
      cargoDetails: {
        ...cargoDetails,
        quantity: quantity === null ? '' : quantity,
        weight: weight === null ? '' : weight
      },
      fromDate: momentToISOLocalDate(fromMoment),
      fromTime: momentToISOLocalTime(fromMoment),
      toDate: momentToISOLocalDate(toMoment),
      toTime: momentToISOLocalTime(toMoment)
    }
  }
}

export function mapFerryPassageToFormValues (fp, ferryService) {
  const {
    id,
    visibleForClient,
    ferryServiceId,
    harbourLocation,
    price,
    bunkerAdjustmentFactor,
    paymentDays,
    additionalInfo
  } = fp
  return {
    id,
    type: OrderPointType.FerryPassage,
    visibleForClient,
    ferryPassageDetails: {
      contractorId: ferryService?.contractorId || null,
      ferryServiceId,
      harbourCoordinates: harbourLocation.text,
      price,
      bunkerAdjustmentFactor,
      paymentDays,
      additionalInfo,
    }
  }
}

export function mapCustomsControlToFormValues (cc) {
  const { id, visibleForClient, ...customsControlDetails } = cc
  return {
    id,
    type: OrderPointType.CustomsControl,
    visibleForClient,
    customsControlDetails
  }
}

export function mapCustomIntermediatePointToFormValues (cip) {
  const { id, visibleForClient, ...customIntermediatePointDetails } = cip
  return {
    id,
    type: OrderPointType.Custom,
    visibleForClient,
    customIntermediatePointDetails
  }
}

export function mapFormValuesToRequestBody (values, lockId) {
  const { carrier, points, emailNotificationSettings, transactions, ...rest } = values
  const orderTransactionCosts = transactions?.costs ? transactions.costs : []
  const orderTransactionIncomes = transactions?.incomes ? transactions.incomes : []
  const orderTransactions = orderTransactionCosts.concat(orderTransactionIncomes)
  return ({
    ...rest,
    deliveryMethod: {
      internal: carrier.isInternal
        ? {
          carrierId: carrier.id,
          carrierContactInfo: carrier.contactInfo,
          ...carrier.internalCar
        }
        : null,
      external: carrier.isInternal === false
        ? {
          carrierId: carrier.id,
          carrierContactInfo: carrier.contactInfo,
          ...carrier.externalCar
        }
        : null
    },
    transactions: orderTransactions,
    emailNotificationSettings: {
      language: emailNotificationSettings.language,
      frequencySettingsType: emailNotificationSettings.scheduledEmailsFrequency.frequencySettingsType,
      sendIntervalHours: emailNotificationSettings.scheduledEmailsFrequency.sendIntervalHours
    },
    dispatcherId: carrier.isInternal ? values.dispatcherId : null,
    loadingDeliveryPoints: points.filter(p => p.type === OrderPointType.Loading).map(mapDeliveryPointFormValuesToRequestBody),
    unloadingDeliveryPoints: points.filter(p => p.type === OrderPointType.Unloading).map(mapDeliveryPointFormValuesToRequestBody),
    intermediatePoints: [
      ...getIntermediatePointSeriesSet(points),
    ],
    timeZone: moment.tz.guess(),
    lockId
  })
}

export function mapEmailNotificationSettingsToFormValues (emailNotificationSettings) {
  return ({
    scheduledEmailsFrequency: scheduledEmailsFrequencyOptionFromEmailNotificationSettings(emailNotificationSettings),
    language: emailNotificationSettings.language
  })
}

function mapDeliveryPointFormValuesToRequestBody (dp) {
  const { fromDate, fromTime, toDate, toTime, ...rest } = dp.deliveryPointDetails
  return ({
    ...rest,
    fromDateTime: momentToISOLocalDateTime(
      parseMomentFromLocalDateAndLocalTimeFields(fromDate, fromTime)
    ),
    toDateTime: momentToISOLocalDateTime(
      parseMomentFromLocalDateAndLocalTimeFields(toDate, toTime)
    )
  })
}

function getIntermediatePointSeriesSet (orderPoints) {
  const entries = orderPoints.map((p, i) => [p, i])
    .filter(([p]) => isIntermediatePoint(p))
    .map(([ip, i]) => {
      let indexOfPrecedingDeliveryPoint = -1
      for (let j = i - 1; j >= 0; j--) {
        const pointType = orderPoints[j].type
        if (pointType === OrderPointType.Loading || pointType === OrderPointType.Unloading) {
          indexOfPrecedingDeliveryPoint = j
          break
        }
      }
      return [ip, indexOfPrecedingDeliveryPoint]
    })
    .reduce((pointsByIndexOfPrecedingDeliveryPoint, [ip, i]) => {
        const points = pointsByIndexOfPrecedingDeliveryPoint.get(i) || []
        points.push(ip)
        pointsByIndexOfPrecedingDeliveryPoint.set(i, points)
        return pointsByIndexOfPrecedingDeliveryPoint
      }, new Map()
    )
    .entries()
  return Array.from(entries, ([i, intermediatePoints]) => {
    const precedingDeliveryPoint = i === -1
      ? null
      : {
        position: getNumberOfPointAtIndex(orderPoints, i),
        type: orderPoints[i].type
      }
    const points = intermediatePoints.map(ip => {
      if (ip.type === OrderPointType.FerryPassage) {
        return mapFerryPassagePointFormValuesToRequestBody(ip)
      }
      if (ip.type === OrderPointType.CustomsControl) {
        return mapCustomsControlPointFormValuesToRequestBody(ip)
      }
      if (ip.type === OrderPointType.Custom) {
        return mapCustomIntermediatePointFormValuesToRequestBody(ip)
      }
      throw new Error('Unknown intermediate point type')
    })
    return { precedingDeliveryPoint, points }
  })
}

const isIntermediatePoint = orderPoint =>
  [OrderPointType.FerryPassage, OrderPointType.CustomsControl, OrderPointType.Custom].includes(orderPoint.type)

const mapFerryPassagePointFormValuesToRequestBody = fp => {
  const {
    ferryServiceId,
    harbourCoordinates,
    paymentDays,
    price,
    bunkerAdjustmentFactor,
    additionalInfo
  } = fp.ferryPassageDetails
  const [lat, lng] = parseCoordinates(harbourCoordinates)

  return {
    id: fp.id,
    type: IntermediatePointType.FerryPassage,
    visibleForClient: fp.visibleForClient,
    ferryServiceId,
    harbourLocation: {
      latitudeMicroDegrees: toMicroDegrees(lat),
      longitudeMicroDegrees: toMicroDegrees(lng)
    },
    paymentDays,
    price,
    bunkerAdjustmentFactor,
    additionalInfo
  }
}

const mapCustomsControlPointFormValuesToRequestBody = cc => {
  return {
    id: cc.id,
    type: IntermediatePointType.CustomsControl,
    visibleForClient: cc.visibleForClient,
    ...(cc.customsControlDetails)
  }
}

const mapCustomIntermediatePointFormValuesToRequestBody = cip =>
  ({
    id: cip.id,
    type: IntermediatePointType.Custom,
    visibleForClient: cip.visibleForClient,
    ...(cip.customIntermediatePointDetails)
  })
